"use client"

import { useState, useEffect } from "react"
import { Menu } from "lucide-react"
import { FaxSidebar } from "@/components/fax-sidebar"
import { FaxTable } from "@/components/fax-table"
import { MobileFaxTable } from "@/components/mobile-fax-table"
import { Organization, User, FaxTableRow } from "@/types"
import { Button } from "@/components/ui/button"
import { createRefreshChannel } from "@/channels/refresh_channel"
import { useMobileDeviceClasses } from "@lsproule/mobile-detect"
import { tracker } from "@openreplay/tracker"
import { SendFax } from "@/components/send-fax"
import { usePage } from "@inertiajs/react"

interface FaxInboxProps {
  faxes: FaxTableRow[]
  organization: Organization
  user: User

  /** server-derived */
  activeCategory: string
  inboxCategories: any[]
  sentCategories: any[]

  /** pagy passthrough */
  pagy: {
    page: number
    last: number
    count: number
  }
}

export default function FaxInbox({
  faxes,
  organization,
  user,
  activeCategory = "inbox",
  inboxCategories,
  sentCategories,
  pagy,
}: FaxInboxProps) {
  useMobileDeviceClasses()


  tracker.setUserID(user?.email);
  console.log("user in FaxInbox:", user)
  const isMobile = typeof window !== "undefined" && window.innerWidth < 1024
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)

  const { props } = usePage()
  const { numbers } = props


  useEffect(() => {
    const wss = createRefreshChannel(["users"])
    return () => wss.unsubscribe()
  }, [])

  const handleArchive = (faxId: number) => {
    fetch("/faxes/archive", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fax_id: faxId }),
    })
  }

  const handleDelete = (faxId: number) => {
    fetch(`/faxes/${faxId}`, {
      method: "DELETE",
    })
  }

  const isInboxView = activeCategory.startsWith("inbox")
  const showActions = !activeCategory.endsWith("trash")

  return (
    <div className="flex h-screen overflow-hidden">
      <FaxSidebar
        user={{ ...user, organization_id: organization.id, organization }}
        activeCategory={activeCategory}
        inboxCategories={inboxCategories}
        sentCategories={sentCategories}
        isMobileOpen={isMobileSidebarOpen}
        onMobileClose={() => setIsMobileSidebarOpen(false)}
      />

      <main className="flex-1 overflow-y-auto">
        <div className="mx-auto md:p-6">
          <div className="flex items-center p-4 gap-3 mb-2">
            <Menu
              onClick={() => setIsMobileSidebarOpen(true)}
              className="md:hidden w-6 h-6 hover:bg-accent rounded-lg"
            />
            <h2 className="text-xl md:text-2xl font-semibold">
              {isInboxView ? "Inbox" : "Sent Faxes"}
            </h2>

            <Button className="ml-auto" onClick={() => setDialogOpen(prev => !prev)}  >Send Fax</Button>
          </div>

          <p className="text-sm text-muted-foreground ml-4 md:ml-0">
            {pagy.count} total faxes • Page {pagy.page} of {pagy.last}
          </p>

          {isMobile ? (
            <MobileFaxTable
              faxes={faxes}
              isMobile
              showFromColumn={isInboxView}
              showActions={showActions}
              onArchive={handleArchive}
              onDelete={handleDelete}
            />
          ) : (
            <FaxTable
              faxes={faxes}
              isMobile={false}
              showFromColumn={isInboxView}
              showActions={showActions}
              onArchive={handleArchive}
              onDelete={handleDelete}
            />
          )}
        </div>
      </main>


      <SendFax
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        onCreateTemplate={() => {
          alert("Navigate to template creation page")
        }}
        onSend={(data) => {
          console.log("[v0] Fax sent:", {
            ...data,
            to: data.to,
            coversheetFile: data.coversheetFile?.name ?? null,
          })
          alert(`Fax queued to ${data.to.length} recipient(s)!`)
        }}
      />

    </div>
  )
}

